import classNames from "classnames";
import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { FaCircleInfo } from "react-icons/fa6";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";

interface BudgetSummaryCardProps {
  label: string;
  value: string;
  subValue?: ReactNode;
  tone?: "default" | "positive" | "negative";
  tooltip?: string;
  helpHref?: string;
}

export function BudgetSummaryCard({
  label,
  value,
  subValue,
  tone,
  tooltip,
  helpHref,
}: BudgetSummaryCardProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex min-w-[108px] flex-1 flex-col gap-0.5 rounded-md border border-border bg-muted/40 px-2.5 py-2"
    >
      <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex text-muted-foreground/80 hover:text-foreground"
                aria-label={`${label} definition`}
              >
                <FaCircleInfo className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-left normal-case tracking-normal">
              <p>{tooltip}</p>
              {helpHref ? (
                <a
                  href={helpHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-secondary underline-offset-2 hover:underline"
                >
                  Learn more
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </span>
      <span
        className={classNames(
          "font-squada text-[22px] leading-none",
          tone === "positive"
            ? "text-success"
            : tone === "negative"
              ? "text-danger"
              : "text-foreground"
        )}
      >
        {value}
      </span>
      {subValue ? (
        <span className="truncate text-[11px] leading-tight text-muted-foreground">{subValue}</span>
      ) : null}
    </div>
  );
}
